import React, { Component } from "react";
import { withStyles } from "@material-ui/core/styles";

import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Grid from "@material-ui/core/Grid";
import TextField from "@material-ui/core/TextField";
import Button from "@material-ui/core/Button";
import FormControl from "@material-ui/core/FormControl";
import FormLabel from "@material-ui/core/FormLabel";
import RadioGroup from "@material-ui/core/RadioGroup";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Radio from "@material-ui/core/Radio";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";

import utils from "../../universal/utils";

const basePath = utils.getEdgeBasePath();

const styles = (theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  fieldRow: {
    marginBottom: theme.spacing(2),
  },
  saveButton: {
    marginTop: theme.spacing(2),
  },
  breakdown: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  breakdownRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "4px 0",
  },
  finalPrice: {
    fontWeight: 700,
    fontSize: "1.2rem",
    color: "#1a5276",
  },
  chipAuto: {
    backgroundColor: "#d4edda",
    color: "#155724",
  },
  chipManual: {
    backgroundColor: "#fff3cd",
    color: "#856404",
  },
  infoText: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginTop: theme.spacing(0.5),
  },
  divider: {
    margin: `${theme.spacing(2)}px 0`,
  },
});

class GeorgiaPricingManager extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      saving: false,
      error: null,
      successMsg: null,

      // Config fields
      fxMode: "auto",
      manualFxRate: 4.0,
      currencyBufferPercent: 12,
      shippingCostPerTireEur: 10,
      vatPercent: 18,
      marginPercent: 55,
      lowLandingCostThresholdEur: 50,
      lowLandingMarginPercent: 125,
      ccFeePercent: 2.5,
      handlingFeeIls: 0,
      rounding: 10,

      // Display data from backend
      fxRate: null,
      fxSource: null,
      breakdown: null,
    };
  }

  componentDidMount() {
    this.fetchConfig();
  }

  fetchConfig = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(`${basePath}/api/v1/admin/georgia/pricing-config`, {
        headers: utils.getAdminAuthHeaders(),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to load");

      const cfg = data.config;
      this.setState({
        fxMode: cfg.fxMode || "manual",
        manualFxRate: cfg.manualFxRate ?? 4.0,
        currencyBufferPercent: cfg.currencyBufferPercent ?? 12,
        shippingCostPerTireEur: cfg.shippingCostPerTireEur ?? 10,
        vatPercent: cfg.vatPercent ?? 18,
        marginPercent: cfg.marginPercent ?? 55,
        lowLandingCostThresholdEur: cfg.lowLandingCostThresholdEur ?? 50,
        lowLandingMarginPercent: cfg.lowLandingMarginPercent ?? 125,
        ccFeePercent: cfg.ccFeePercent ?? 2.5,
        handlingFeeIls: cfg.handlingFeeIls ?? 0,
        rounding: cfg.rounding ?? 10,
        fxRate: data.fxRate,
        fxSource: data.fxSource,
        breakdown: data,
        loading: false,
      });
    } catch (err) {
      this.setState({ error: err.message, loading: false });
    }
  };

  handleChange = (field) => (e) => {
    this.setState({ [field]: e.target.value, successMsg: null });
  };

  handleSave = async () => {
    this.setState({ saving: true, error: null, successMsg: null });
    try {
      const {
        fxMode,
        manualFxRate,
        currencyBufferPercent,
        shippingCostPerTireEur,
        vatPercent,
        marginPercent,
        lowLandingCostThresholdEur,
        lowLandingMarginPercent,
        ccFeePercent,
        handlingFeeIls,
        rounding,
      } =
        this.state;

      const res = await fetch(`${basePath}/api/v1/admin/georgia/pricing-config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...utils.getAdminAuthHeaders(),
        },
        body: JSON.stringify({
          fxMode,
          manualFxRate: Number(manualFxRate),
          currencyBufferPercent: Number(currencyBufferPercent),
          shippingCostPerTireEur: Number(shippingCostPerTireEur),
          vatPercent: Number(vatPercent),
          marginPercent: Number(marginPercent),
          lowLandingCostThresholdEur: Number(lowLandingCostThresholdEur),
          lowLandingMarginPercent: Number(lowLandingMarginPercent),
          ccFeePercent: Number(ccFeePercent),
          handlingFeeIls: Number(handlingFeeIls),
          rounding: Number(rounding),
        }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error || "Failed to save");

      const cfg = data.config;
      const sample = data.sample;
      this.setState({
        fxMode: cfg.fxMode,
        manualFxRate: cfg.manualFxRate,
        currencyBufferPercent: cfg.currencyBufferPercent,
        shippingCostPerTireEur: cfg.shippingCostPerTireEur,
        vatPercent: cfg.vatPercent,
        marginPercent: cfg.marginPercent,
        lowLandingCostThresholdEur: cfg.lowLandingCostThresholdEur,
        lowLandingMarginPercent: cfg.lowLandingMarginPercent,
        ccFeePercent: cfg.ccFeePercent,
        handlingFeeIls: cfg.handlingFeeIls,
        rounding: cfg.rounding,
        fxRate: sample.fxRate,
        fxSource: sample.fxSource,
        breakdown: sample,
        saving: false,
        successMsg: "הגדרות המחיר נשמרו בהצלחה!",
      });
    } catch (err) {
      this.setState({ error: err.message, saving: false });
    }
  };

  renderBreakdown() {
    const { classes } = this.props;
    const { breakdown, fxSource } = this.state;
    if (!breakdown) return null;

    return (
      <div className={classes.breakdown}>
        <Typography variant="subtitle2" gutterBottom>
          דוגמת חישוב מחיר — ספק 100 EUR לצמיג יחיד
        </Typography>
        <div className={classes.breakdownRow}>
          <span>שער EUR/ILS בסיס ({fxSource === "auto" ? "אוטומטי" : "ידני"})</span>
          <span>₪{Number(breakdown.fxRate).toFixed(3)}</span>
        </div>
        <div className={classes.breakdownRow}>
          <span>באפר הגנת מטבע ({this.state.currencyBufferPercent}%)</span>
          <span>₪{Number(breakdown.protectedFxRate).toFixed(3)}</span>
        </div>
        <div className={classes.breakdownRow}>
          <span>עלות משלוח לצמיג</span>
          <span>€{breakdown.shippingCostPerTireEur}</span>
        </div>
        <div className={classes.breakdownRow}>
          <span>עלות נחתת לפני רווח</span>
          <span>€{breakdown.landedCostEur}</span>
        </div>
        <div className={classes.breakdownRow}>
          <span>בסיס בש״ח (עלות נחתת × שער מוגן)</span>
          <span>₪{breakdown.baseIls}</span>
        </div>
        <div className={classes.breakdownRow}>
          <span>אחרי מרג׳ין בפועל ({breakdown.effectiveMarginPercent ?? this.state.marginPercent}%)</span>
          <span>₪{breakdown.afterMarginIls}</span>
        </div>
        <div className={classes.breakdownRow}>
          <span>
            אחרי קופון {breakdown.recoveryCouponPercent ?? 5}% נשארים{" "}
            {breakdown.marginAfterCouponPercent ?? "—"}% רווח
            {breakdown.couponFloorApplied ? " (הרצפה הרימה את המחיר)" : ""}
          </span>
          <span>מינימום {breakdown.minMarginAfterCouponPercent ?? 10}%</span>
        </div>
        <div className={classes.breakdownRow}>
          <span>שכבת מרג׳ין נמוך</span>
          <span>
            {breakdown.lowLandingActive
              ? `פעילה (${this.state.lowLandingMarginPercent}%)`
              : "לא פעילה בדוגמה"}
          </span>
        </div>
        <div className={classes.breakdownRow}>
          <span>אחרי עמלת כ"א ({this.state.ccFeePercent}%)</span>
          <span>₪{breakdown.afterCcFeeIls}</span>
        </div>
        <div className={classes.breakdownRow}>
          <span>אחרי מע"מ ({this.state.vatPercent}%)</span>
          <span>₪{breakdown.afterVatIls}</span>
        </div>
        {Number(this.state.handlingFeeIls) > 0 && (
          <div className={classes.breakdownRow}>
            <span>תוספת טיפול</span>
            <span>₪{breakdown.afterHandlingIls}</span>
          </div>
        )}
        <Divider className={classes.divider} />
        <div className={classes.breakdownRow}>
          <span>
            <strong>מחיר סופי לצרכן (עיגול ל-{this.state.rounding})</strong>
          </span>
          <span className={classes.finalPrice}>₪{breakdown.finalRetailIls}</span>
        </div>
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    const { loading, saving, error, successMsg, fxMode, fxRate, fxSource } = this.state;

    if (loading) {
      return (
        <div style={{ textAlign: "center", padding: 40 }}>
          <CircularProgress />
        </div>
      );
    }

    return (
      <div className={classes.root}>
        <Paper className={classes.section}>
          <Typography variant="h6" gutterBottom>
            הגדרות מחיר — מלאי גאורגיה (אירופה)
          </Typography>
          <Typography variant="body2" color="textSecondary" paragraph>
            הנוסחה: (מחיר ספק + משלוח לצמיג) × שער מוגן × (1 + מרג׳ין בפועל) × (1 + עמלת כ״א) × (1 + מע״מ) + דמי טיפול → מעוגל
          </Typography>

          <Grid container spacing={3}>
            {/* FX Mode */}
            <Grid item xs={12} md={6}>
              <FormControl component="fieldset" className={classes.fieldRow}>
                <FormLabel component="legend">מצב שער חליפין EUR/ILS</FormLabel>
                <RadioGroup
                  row
                  value={fxMode}
                  onChange={this.handleChange("fxMode")}
                >
                  <FormControlLabel value="manual" control={<Radio />} label="ידני" />
                  <FormControlLabel value="auto" control={<Radio />} label="אוטומטי (frankfurter.app, יומי)" />
                </RadioGroup>
              </FormControl>

              {fxRate && (
                <div style={{ marginBottom: 12 }}>
                  <Chip
                    size="small"
                    label={`שער נוכחי: ₪${Number(fxRate).toFixed(3)}`}
                    className={fxSource === "auto" ? classes.chipAuto : classes.chipManual}
                  />
                  <Typography className={classes.infoText}>
                    {fxSource === "auto" ? "נשלף אוטומטית, מאוחסן 24 שעות" : "שער ידני שהוגדר"}
                  </Typography>
                </div>
              )}

              <TextField
                label="שער ידני EUR → ILS"
                type="number"
                value={this.state.manualFxRate}
                onChange={this.handleChange("manualFxRate")}
                className={classes.fieldRow}
                fullWidth
                disabled={fxMode === "auto"}
                inputProps={{ step: "0.001", min: "0" }}
                helperText="ישמש רק כשמצב שער = ידני"
              />
              <TextField
                label="באפר הגנת מטבע (%)"
                type="number"
                value={this.state.currencyBufferPercent}
                onChange={this.handleChange("currencyBufferPercent")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "0.1", min: "0" }}
                helperText="לדוגמה: 12% על שער 3.40 יחשב לפי 3.808"
              />
            </Grid>

            {/* Pricing params */}
            <Grid item xs={12} md={6}>
              <TextField
                label="עלות משלוח לצמיג (EUR)"
                type="number"
                value={this.state.shippingCostPerTireEur}
                onChange={this.handleChange("shippingCostPerTireEur")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "0.1", min: "0" }}
                helperText="מתווסף למחיר הספק לפני חישוב הרווח"
              />
              <TextField
                label="מרג׳ין רגיל (%)"
                type="number"
                value={this.state.marginPercent}
                onChange={this.handleChange("marginPercent")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "0.1", min: "0" }}
                helperText="ברירת מחדל לכל מוצר שלא נכנס לשכבת עלות נחתת נמוכה"
              />
              <TextField
                label="סף עלות נחתת נמוכה (EUR)"
                type="number"
                value={this.state.lowLandingCostThresholdEur}
                onChange={this.handleChange("lowLandingCostThresholdEur")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "1", min: "0" }}
                helperText="0 = כבוי. מרג׳ין עלות נמוכה חל כאשר עלות נחתת כוללת ≤ הסף"
              />
              <TextField
                label="מרג׳ין לעלות נחתת נמוכה (%)"
                type="number"
                value={this.state.lowLandingMarginPercent}
                onChange={this.handleChange("lowLandingMarginPercent")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "0.1", min: "0" }}
                helperText="חל כאשר עלות הנחתת בסף או מתחתיו"
              />
              <TextField
                label="עמלת כרטיס אשראי (%)"
                type="number"
                value={this.state.ccFeePercent}
                onChange={this.handleChange("ccFeePercent")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "0.1", min: "0" }}
              />
              <TextField
                label='מע"מ (%)'
                type="number"
                value={this.state.vatPercent}
                onChange={this.handleChange("vatPercent")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "0.1", min: "0" }}
              />
              <TextField
                label="תוספת טיפול קבועה (₪)"
                type="number"
                value={this.state.handlingFeeIls}
                onChange={this.handleChange("handlingFeeIls")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "1", min: "0" }}
                helperText="0 = ללא תוספת"
              />
              <TextField
                label="עיגול (ILS) — 0 = ללא"
                type="number"
                value={this.state.rounding}
                onChange={this.handleChange("rounding")}
                className={classes.fieldRow}
                fullWidth
                inputProps={{ step: "5", min: "0" }}
                helperText="לדוגמה: 10 מעגל ל-10 ₪ הקרוב"
              />
            </Grid>
          </Grid>

          {this.renderBreakdown()}

          {error && (
            <Typography color="error" style={{ marginTop: 12 }}>
              שגיאה: {error}
            </Typography>
          )}
          {successMsg && (
            <Typography style={{ marginTop: 12, color: "#155724" }}>
              {successMsg}
            </Typography>
          )}

          <Button
            variant="contained"
            color="primary"
            className={classes.saveButton}
            onClick={this.handleSave}
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "שמור הגדרות"}
          </Button>
        </Paper>
      </div>
    );
  }
}

export default withStyles(styles)(GeorgiaPricingManager);
